import { Component, effect, inject, OnInit, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { Router } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { PresenceResponse, SignalrPresenceService } from 'src/app/service/signalr-presence.service';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { ManageAccessPopupComponent } from '../../../public/manage-access-popup/manage-access-popup.component';
import { BranchService } from '../../../service/branch.service';
import { LoggedInCallback } from '../../../service/cognito.service';
import { CustomerPortalService } from '../../../service/customer-portal.service';
import { PosthogService } from '../../../service/posthog.service';
import { UserLoginService } from '../../../service/user-login.service';
import { BranchExtended as Branch, Camera, FeatureFlag, LocationInfo, LocationPlanMap, SelfServeTheftPlanPayload } from '../../../types';
import { ManageAccessPopupData } from '../../../types/access';

@Component({
  selector: 'app-customer-portal',
  templateUrl: './customer-portal.component.html',
  styleUrls: ['./customer-portal.component.scss'],
  standalone: false,
})
export class CustomerPortalComponent implements OnInit, LoggedInCallback {
  router = inject(Router);
  private branchService = inject(BranchService);
  private subscriptionService = inject(SubscriptionService);
  private customerPortalService = inject(CustomerPortalService);
  dialog = inject(MatDialog);
  userService = inject(UserLoginService);
  private posthogService = inject(PosthogService);
  private snackBar = inject(MatSnackBar);
  private presenceService = inject(SignalrPresenceService);

  readonly sort = viewChild(MatSort);

  public selectedBranch: Branch;
  public selectedLocationPlanMap: LocationPlanMap = {};
  public editMode = false;
  public branches: Branch[];
  public saving = false;
  public expandedBranchId: number;

  private readModeColumns: string[] = [
    'location_name',
    'external_id',
    'flagged',
    'camera_status',
    'lp_plan',
    'report_link',
    'manage_access',
    'history',
  ];
  private editModeColumns: string[] = ['location_name', 'external_id', 'lp_plan', 'history'];
  public displayedColumns: string[] = [];

  public filterBranches: Branch[];
  public viewBillingFeaturePayload: number[] = [];

  public _selfServePrices = new Map<string, any[]>();

  public selfServeBranches: number[] = [];

  // TODO: add type
  private stripeProductPrices: any[] = [];
  public isLoading: boolean = false;

  // This is the support ticket request that app-footer's help-dialog should call.
  public presence: PresenceResponse = [null, []];
  private presenceSubscription?: Subscription = null;

  constructor() {
    effect(() => {
      const isReady = this.posthogService.isReady();
      if (!isReady) return;

      const viewBillingFlag = this.posthogService.getFeatureFlagPayloadIfEnabled<number[]>('viewBillingFeature', []);
      if (viewBillingFlag.enabled) {
        this.viewBillingFeaturePayload = viewBillingFlag.payload;
      }

      const selfServeFlag = this.posthogService.getFeatureFlagPayloadIfEnabled<SelfServeTheftPlanPayload>('self_serve_theft_plan', {});

      let theftPlanPrices = null;
      if (selfServeFlag.enabled) {
        const { theft_plan_prices, branches } = selfServeFlag.payload;
        theftPlanPrices = theft_plan_prices;
        this.selfServeBranches = branches ?? [];
      }

      this.branchService.getBranches().subscribe((branchesResponse) => {
        this.displayedColumns = this.readModeColumns;
        this.branches = branchesResponse.items;
        this.filterBranches = this.branches.map((b) => ({ ...b }));
        // Retrieve theft plans ahead of time, we'll use it in "Loss Prevention Plan" column
        Array.from(new Set(this.branches.map((b) => b.customer_type))).forEach((customerType) =>
          this.subscriptionService.getTheftPlans(customerType).subscribe((prices: any[]) => {
            this.stripeProductPrices.push(...prices);
            this._selfServePrices.set(
              customerType,
              prices.filter((price) => theftPlanPrices?.includes(price.id)),
            );
          }),
        );
      });
    });
  }

  evaluateViewBilling(id) {
    return this.viewBillingFeaturePayload.includes(id);
  }

  isAccessSuspended(branch: Branch | undefined): boolean {
    return branch?.keystore?.suspend_access ?? false;
  }

  ngOnInit(): void {
    this.presenceService.connectPresencesHub().subscribe();
  }

  getSelfServePrices(customerType, locationId) {
    const prices = this._selfServePrices.get(customerType) ?? [];
    const values = [...prices];

    const locationPriceId = this.selectedLocationPlanMap[locationId]?.current.price_id;
    if (locationPriceId && !values.find((price) => price.id === locationPriceId)) {
      const locationPrice = this.stripeProductPrices.find((price) => price.id === locationPriceId);
      values.unshift(locationPrice);
    }
    return values;
  }

  getProperty = (obj, path) => {
    const result = path.split('.').reduce((o, p) => o && o[p], obj);

    return result;
  };

  applySort(event: Sort, branch: Branch, table: MatTable<LocationInfo>) {
    function compare(direction: SortDirection, a: any, b: any) {
      // swap variables if we're sorting descending
      if (direction === 'desc') {
        [a, b] = [b, a];
      }

      // compare using locale specific if its a string, otherwise built-in
      if (typeof a === 'string') {
        return a.localeCompare(b);
      } else if (typeof b === 'boolean') {
        return a === b ? 0 : a < b ? 1 : -1;
      } else {
        return a === b ? 0 : a < b ? -1 : 1;
      }
    }

    branch.locations = (branch.locations ?? []).sort((a, b) =>
      compare(event.direction, this.getProperty(a, event.active), this.getProperty(b, event.active)),
    );

    table.renderRows();
  }

  applySearchLocation(event: Event, displayedBranch: Branch, table: MatTable<LocationInfo>) {
    const filterValue = (event.target as HTMLInputElement).value.toLocaleLowerCase();
    const originalBranch = this.branches.find((b) => b.id === displayedBranch.id);
    displayedBranch.locations = originalBranch.locations.filter(
      (location) =>
        location.location_name.toLocaleLowerCase().includes(filterValue) ||
        location.external_id?.toLocaleLowerCase().includes(filterValue) ||
        String(location.location_id).includes(filterValue),
    );
    table.renderRows();
  }

  isLoggedIn(): void {}

  humanFileSize(size: number): string {
    if (!size) {
      return '0 B';
    }
    const i = Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  }

  getCameraTooltipText(camera: Camera): string {
    return `Name: ${camera.camera_name}
            Serial Number: ${camera.serial_number}
            UUID: ${camera.camera_uuid}
            Bandwidth: ${this.humanFileSize(camera.bandwidth)}
            Health: ${camera.health_status}
            Connection: ${camera.health_status}`;
  }

  showFilter(branch: Branch) {
    const originalBranch = this.branches.find((b) => b.id === branch.id);
    return originalBranch.locations && originalBranch.locations.length > 10;
  }

  onManageAccessClick(branch: Branch, location: LocationInfo) {
    this.dialog.open<ManageAccessPopupComponent, ManageAccessPopupData>(ManageAccessPopupComponent, {
      width: '800px',
      maxHeight: '95vh',
      panelClass: 'manage-access-container',
      data: {
        branchId: branch.id,
        branchName: branch.name,
        locationName: location.location_name,
        location_id: location.location_id,
      },
    });
  }

  onManageBillingClicked(branch: Branch, event?: Event) {
    event?.stopPropagation();

    const useFlywire = this.posthogService.isReady() && this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableNetsuiteMigration);

    const session$ = useFlywire
      ? this.customerPortalService.createFlywireSession(branch.id)
      : this.customerPortalService.createSession(branch.stripe_customer_id);

    session$.subscribe({
      next: (response) => {
        window.open(response.url, '_blank');
      },
    });
  }

  private memoizedProductLabel = {};
  getStripePlanLabel(priceId: string) {
    if (this.memoizedProductLabel[priceId] !== undefined) {
      return this.memoizedProductLabel[priceId];
    }
    const price = this.stripeProductPrices.find((p) => p.id === priceId);
    if (price) {
      this.memoizedProductLabel[priceId] = price.product.name.toLowerCase();
      return this.memoizedProductLabel[priceId];
    }

    return null;
  }

  onEditClicked(branch: Branch) {
    const roomName = `SUBSCRIPTION_EDIT_${branch.id}`;
    this.presenceSubscription = this.presenceService.joinRoom(roomName).subscribe({
      next: (data: PresenceResponse) => {
        this.presence = data;
      },
    });
    this.selectedBranch = structuredClone(branch);
    this.displayedColumns = this.editModeColumns;
    this.editMode = true;

    this.selectedLocationPlanMap = {};
    this.selectedBranch.locations?.forEach((location) => {
      this.selectedLocationPlanMap[location.location_id] = {
        current: {
          price_id: location.stripe_price_id,
        },
        previous: {
          price_id: location.stripe_price_id,
        },
      };
    });
  }

  onCancelClicked() {
    this.selectedBranch = null;
    this.editMode = false;
    this.displayedColumns = this.readModeColumns;
    this.presenceSubscription?.unsubscribe();
  }

  onSaveClicked(branch: Branch) {
    if (this.saving) {
      return;
    }

    this.posthogService.capture('update_theft_plan', {
      admin: false,
      branchId: branch.id,
      planMap: this.selectedLocationPlanMap,
    });

    this.saving = true;
    this.subscriptionService
      .updateSubscription({
        branch_id: branch.id,
        location_plans: Object.keys(this.selectedLocationPlanMap).map((key) => ({
          location_id: parseInt(key),
          location_plan: this.selectedLocationPlanMap[key].current,
        })),
        previous_plans: Object.keys(this.selectedLocationPlanMap).map((key) => ({
          location_id: parseInt(key),
          location_plan: this.selectedLocationPlanMap[key].previous,
        })),
      })
      .subscribe({
        next: () => {
          this.branches
            .find((b) => b.id === branch.id)
            .locations?.forEach((location) => {
              if (this.selectedLocationPlanMap[location.location_id]) {
                location.stripe_price_id = this.selectedLocationPlanMap[location.location_id].current.price_id;
              }
            });

          this.editMode = false;
          this.displayedColumns = this.readModeColumns;
          this.saving = false;
          this.selectedBranch = null;
          this.presenceSubscription?.unsubscribe();
        },
        error: (error) => {
          if (error.error?.error.includes('Please refresh')) {
            this.snackBar.open(error.error.error, 'Dismiss', { duration: 5000 });
          } else if (error.error?.title) {
            this.snackBar.open(error.error.title, 'Dismiss', { duration: 5000 });
          } else {
            this.snackBar.open('Error saving subscription plan. Please contact support', 'Dismiss', { duration: 5000 });
          }

          this.saving = false;
        },
      });
  }

  onExpandBranch(branchId: number) {
    this.expandedBranchId = branchId;
    const branch = this.branches.find((b) => b.id === branchId);
    if (this.isAccessSuspended(branch)) return;

    this.isLoading = true;
    this.branchService.deprecated_getLocationsForBranch(branchId).subscribe((locations) => {
      branch.locations = locations;
      this.filterBranches.find((b) => b.id === branchId).locations = locations;
      this.isLoading = false;
    });
  }

  isLocationEditable(location: LocationInfo) {
    return new Date() < new Date(location.trial_end);
  }

  getFlaggedTooltip(location: LocationInfo) {
    if (!location.flagged) {
      return '';
    }

    return `${location.flagged_reasons?.join(', ') || 'Unspecified reason'} (${moment(location.flagged_at).format('lll')})`;
  }
}
